import type { Location } from "history";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import { DynamicLayout } from "@circle-react/components/Layout/DynamicLayout";
import { LeaderboardPage } from "@circle-react/components/Leaderboard";
import { PostModalComponent } from "@circle-react/components/PostsV3/PostModal";
import { CoursesPage } from "@circle-react/pages/Courses";
import { EventsPage } from "@circle-react/pages/Events";
import { HomePageV2 } from "@circle-react/pages/Home";
import { MembersPage } from "@circle-react/pages/Members";
import { PostPage } from "@circle-react/pages/Post";
import { ProfilePage } from "@circle-react/pages/Profile";
import { SpacePage } from "@circle-react/pages/Space";
import { SpaceGroupPage } from "@circle-react/pages/SpaceGroup";
import { ProtectedRoute } from "@circle-react-shared/ProtectedRoute";

const spacePaths = [
  "/c/:spaceSlug",
  "/c/:spaceSlug/invite",
  "/c/:spaceSlug/members",
  "/c/:spaceSlug/customize",
  "/c/:spaceSlug/customize/*",
  "/c/:spaceSlug/sections/*",
  "/c/:spaceSlug/edit-lesson/*",
];

interface LocationState {
  prevLocation?: Location;
  shouldFocusOnCommentsSection?: boolean;
}

export const RootRoute = () => {
  const location = useLocation<LocationState>();
  const { state } = location;
  const { prevLocation } = state ?? {};
  const {
    post_login_redirect: postLoginRedirectUrlParam,
  }: { post_login_redirect?: string } = useRouterQueryParams();

  const redirectUrl = () => {
    if (postLoginRedirectUrlParam) {
      return `/feed?post_login_redirect=${encodeURIComponent(postLoginRedirectUrlParam)}`;
    }
    return "/feed";
  };

  return (
    <Route path="/">
      <DynamicLayout disableRightSidebar>
        <Switch location={prevLocation || location}>
          <Route component={HomePageV2} path="/feed" exact />
          <Redirect from="/home" to={redirectUrl()} />
          <ProtectedRoute component={MembersPage} path="/members" exact />
          <ProtectedRoute component={EventsPage} path="/events" exact />
          <ProtectedRoute component={CoursesPage} path="/courses" exact />
          <ProtectedRoute
            component={LeaderboardPage}
            path="/leaderboard"
            exact
          />
          <ProtectedRoute component={ProfilePage} path="/u/:publicUid" />
          <ProtectedRoute
            component={SpaceGroupPage}
            path="/s/:spaceGroupSlug"
          />
          <ProtectedRoute component={SpacePage} path={spacePaths} exact />
          <ProtectedRoute component={PostPage} path="/c/:spaceSlug/:postSlug" />
        </Switch>
        {prevLocation && (
          <ProtectedRoute
            component={PostModalComponent}
            path="/c/:spaceSlug/:postSlug"
          />
        )}
      </DynamicLayout>
    </Route>
  );
};
